import { useState, useEffect } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Wrench, Warning, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

export default function TenantRepairPage() {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const runScan = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/tenant-repair/scan");
      setScan(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runScan(); }, []);

  const execute = async () => {
    if (!window.confirm(`Repair ${scan.counts.users_to_repair} user record(s)? This updates their tenant_id to match yours and cannot be undone automatically.`)) return;
    setRunning(true);
    try {
      const { data } = await API.post("/admin/tenant-repair/execute");
      setResult(data);
      toast.success(`Repaired ${data.repaired_user_count} record(s)`);
      await runScan();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Repair failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="tenant-repair-page">
      <div className="flex items-center gap-3 mb-2">
        <Wrench size={26} className="text-amber-600" />
        <h1 className="text-2xl font-[Chivo] font-bold">Tenant Data Repair</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Scans every operative, vendor, and client you invited and checks whether their tenant_id matches yours.
        Records that have drifted (usually legacy data or migration artefacts) cause assignment/listing bugs. This tool fixes them in one pass.
      </p>

      <div className="border border-border rounded-md p-4 bg-card space-y-4" data-testid="scan-results">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Your tenant</p>
            <p className="font-mono text-sm">{scan?.admin_tenant_id || "—"}</p>
          </div>
          <Button size="sm" variant="outline" onClick={runScan} disabled={loading} data-testid="rescan-btn">
            <ArrowsClockwise size={14} className="mr-1" /> Re-scan
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Scanning…</p>
        ) : !scan ? (
          <p className="text-sm text-muted-foreground">No scan data available.</p>
        ) : scan.counts.users_to_repair === 0 ? (
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-md p-3" data-testid="clean-state">
            <CheckCircle size={18} weight="fill" />
            <div>
              <p className="text-sm font-semibold">All clean</p>
              <p className="text-xs">No user records need repair.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
              <Warning size={18} weight="fill" />
              <div>
                <p className="text-sm font-semibold">{scan.counts.users_to_repair} record(s) have drift</p>
                <p className="text-xs">These were invited by you but currently have a different (or empty) tenant_id.</p>
              </div>
            </div>
            <div className="overflow-x-auto border border-border rounded-md">
              <table className="w-full text-left text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Role</th>
                    <th className="px-3 py-2">Current tenant_id</th>
                    <th className="px-3 py-2">Will become</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {scan.users.map(u => (
                    <tr key={u._id} data-testid={`drift-row-${u._id}`}>
                      <td className="px-3 py-2 font-medium">{u.name || "—"}</td>
                      <td className="px-3 py-2">{u.email}</td>
                      <td className="px-3 py-2 font-mono">{u.role}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">{u.current_tenant_id || <span className="italic">empty</span>}</td>
                      <td className="px-3 py-2 font-mono text-emerald-700 dark:text-emerald-400">{u.target_tenant_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={execute} disabled={running} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="repair-btn">
              {running ? "Repairing…" : `Repair ${scan.counts.users_to_repair} record(s)`}
            </Button>
          </>
        )}

        {result && (
          <div className="mt-4 text-xs text-muted-foreground border-t border-border pt-4" data-testid="repair-result">
            <p className="font-semibold text-foreground mb-1">Last run</p>
            <p>Repaired {result.repaired_user_count} user record(s).</p>
          </div>
        )}
      </div>
    </div>
  );
}
